import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Tooltip,
  TextField,
} from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';
import {
  UseFormRegister,
  UseFormWatch,
  UseFormSetValue,
  UseFormGetValues,
  FieldErrors,
} from 'react-hook-form';
import TaxonomyContext from '../TaxonomyContext';
import FileUpload from '../FileUpload';
import AIMetadataGenerator from '../../common/AIMetadataGenerator';
import { FileUploadResponse } from '../../../types/asset.types';
import { environmentSafeLog } from '../../../utils/environment';

// Form data interface (matching RegisterAssetPage)
interface FormData {
  layer: string;
  layerName: string;
  categoryCode: string;
  categoryName: string;
  subcategoryCode: string;
  subcategoryName: string;
  subcategoryNumericCode: string;
  name: string;
  description: string;
  source: string;
  tags: string[];
  files: File[];
  hfn: string;
  mfa: string;
  sequential: string;
  trainingData?: any;
  layerSpecificData?: {
    components: any[];
  };
  albumArtUrl?: string;
  albumArtSource?: string;
  albumArtQuality?: string;
  extractedSongData?: any;
  bpm?: number;
  songMetadata?: any;
  starMetadata?: any;
  lookMetadata?: any;
  looksMetadata?: any;
  moveMetadata?: any;
  movesMetadata?: any;
  worldMetadata?: any;
  worldsMetadata?: any;
  compositeMetadata?: any;
  aiMetadata?: any;
}

interface FileUploadStepProps {
  // Form methods from React Hook Form
  register: UseFormRegister<FormData>;
  watch: UseFormWatch<FormData>;
  setValue: UseFormSetValue<FormData>;
  getValues: UseFormGetValues<FormData>;
  errors: FieldErrors<FormData>;

  // File handling callbacks
  onFilesChange: (files: File[]) => void;
  onUploadProgress: (fileId: string, progress: number) => void;
  onUploadComplete: (fileId: string, fileData: FileUploadResponse) => void;
  onUploadError: (fileId: string, error: string) => void;
  onSourceChange: (source: string) => void;

  // AI callbacks
  onAIDescriptionGenerated: (description: string) => void;
  onAITagsGenerated: (tags: string[]) => void;
  onAIAdditionalMetadataGenerated: (metadata: any) => void;

  // Data
  uploadedFiles: FileUploadResponse[];

  // Layer guidance functions
  getLayerSpecificLabel: (layer: string) => string;
  getLayerSpecificPlaceholder: (layer: string) => string;
  getLayerSpecificGuidance: (layer: string) => string;

  // Status
  disabled?: boolean;
  loading?: boolean;
  error?: string;

  // AI trigger
  triggerAIGeneration?: number;
}

/**
 * Step 3: File Upload + Creator's Description + AI Generation
 *
 * This component handles the most complex step in asset registration:
 * - File upload with drag & drop
 * - Creator's Description input with layer-specific guidance
 * - AI metadata generation integration
 * - Taxonomy context display
 * - Form validation and error handling
 */
const FileUploadStep: React.FC<FileUploadStepProps> = ({
  register,
  watch,
  setValue,
  getValues,
  errors,
  onFilesChange,
  onUploadProgress,
  onUploadComplete,
  onUploadError,
  onSourceChange,
  onAIDescriptionGenerated,
  onAITagsGenerated,
  onAIAdditionalMetadataGenerated,
  uploadedFiles,
  getLayerSpecificLabel,
  getLayerSpecificPlaceholder,
  disabled = false,
  loading = false,
  error,
  triggerAIGeneration,
}) => {
  // Extract current form values
  const watchLayer = watch('layer');
  const watchCategoryCode = watch('categoryCode');
  const watchSubcategoryCode = watch('subcategoryCode');
  const watchName = watch('name') || '';
  const watchStarMetadata = watch('starMetadata');
  
  // CRITICAL FIX: Ensure field value is always current and synced
  // This useEffect ensures that any changes to the field are immediately reflected
  useEffect(() => {
    const currentValue = watch('name');
    if (currentValue !== undefined) {
      // Log when field value changes to help debug sync issues
      environmentSafeLog('[FIELD SYNC] Creator Description field value updated:', {
        value: currentValue,
        length: currentValue?.length || 0,
        timestamp: new Date().toISOString(),
      });
    }
  }, [watchName, watch]);

  // Dynamic Creator's Description label based on asset type
  const getDynamicCreatorLabel = () => {
    if (watchLayer === 'S' && watchStarMetadata?.assetType === 'variant') {
      return "Creator's Description (Variant Details)";
    }
    return getLayerSpecificLabel(watchLayer);
  };

  // FORCE DEBUG: Always log FileUploadStep rendering
  environmentSafeLog('[FORCE DEBUG] FileUploadStep rendered:', {
    watchLayer,
    watchCategoryCode,
    watchSubcategoryCode,
    uploadedFilesCount: uploadedFiles.length,
    creatorDescription: watch('name'),
    hasCreatorDescription: !!watch('name')?.trim(),
  });

  // Error display
  if (error) {
    return (
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            p: 2,
            bgcolor: 'error.light',
            color: 'error.contrastText',
            borderRadius: 1,
            mb: 2,
          }}
        >
          {error}
        </Box>
      </Box>
    );
  }

  const content = (
    <Box sx={{ position: 'relative' }}>
      {/* Display the taxonomy context for reference - compressed and sticky for better UX */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: 'background.paper',
          borderBottom: '1px solid #e0e0e0',
          pb: 1,
          mb: 2,
        }}
      >
        <TaxonomyContext
          layer={watchLayer}
          layerName={getValues('layerName')}
          categoryCode={watchCategoryCode}
          categoryName={getValues('categoryName')}
          subcategoryCode={watchSubcategoryCode}
          subcategoryName={getValues('subcategoryName')}
          compressed={true}
        />
      </Box>

      {/* File Upload Component */}
      <FileUpload
        onFilesChange={onFilesChange}
        layerCode={watchLayer}
        maxFiles={1}
        maxSize={32 * 1024 * 1024} // 32MB for all asset types
        onUploadProgress={onUploadProgress}
        onUploadComplete={onUploadComplete}
        onUploadError={onUploadError}
        initialFiles={getValues('files')}
        initialSource={getValues('source')}
        onSourceChange={onSourceChange}
      />

      {/* Variant Name Field - MOVED AFTER FILE UPLOAD */}
      {(() => {
        // Helper function for variant examples
        const getVariantExamples = (layer: string): string[] => {
          const examples = {
            S: ['Pink Hair', 'Evening Makeup', 'Casual Style', 'Stage Costume'],
            L: [
              'Summer Version',
              'Formal Variant',
              'Color Swap',
              'Seasonal Update',
            ],
            M: ['Slow Tempo', 'High Energy', 'Beginner Level', 'Group Version'],
            W: [
              'Night Scene',
              'Winter Setting',
              'Urban Version',
              'Minimalist Style',
            ],
            G: ['Acoustic Version', 'Remix', 'Extended Mix', 'Cover Version'],
            C: [
              'Extended Cut',
              'Short Form',
              'Alternative Edit',
              'Remix Version',
            ],
          };
          return (
            examples[layer as keyof typeof examples] || [
              'Version 2',
              'Alternative',
              'Updated',
            ]
          );
        };

        // Get current variant data based on layer
        const getCurrentVariantData = () => {
          switch (watchLayer as string) {
            case 'G':
              return watch('songMetadata');
            case 'S':
              return watch('starMetadata');
            case 'L':
              return watch('looksMetadata');
            case 'M':
              return watch('movesMetadata');
            case 'W':
              return watch('worldsMetadata');
            case 'B':
              return watch('compositeMetadata');
            case 'C':
              return watch('compositeMetadata');
            default:
              return null;
          }
        };

        const currentVariantData = getCurrentVariantData();
        const isVariant = currentVariantData?.assetType === 'variant';

        // Only show Variant Name field if this is a variant asset
        if (!isVariant) return null;

        return (
          <Box mt={2} mb={2}>
            <TextField
              fullWidth
              label="Variant Name"
              value={currentVariantData?.variantName || ''}
              onChange={event => {
                const newVariantName = event.target.value;

                // Update the appropriate metadata field based on layer
                const setLayerMetadata = (metadata: any) => {
                  switch (watchLayer as string) {
                    case 'G':
                      return setValue('songMetadata', metadata);
                    case 'S':
                      return setValue('starMetadata', metadata);
                    case 'L':
                      return setValue('looksMetadata', metadata);
                    case 'M':
                      return setValue('movesMetadata', metadata);
                    case 'W':
                      return setValue('worldsMetadata', metadata);
                    case 'B':
                      return setValue('compositeMetadata', metadata);
                    case 'C':
                      return setValue('compositeMetadata', metadata);
                    default:
                      return;
                  }
                };

                const updatedMetadata = {
                  ...currentVariantData,
                  variantName: newVariantName,
                };

                setLayerMetadata(updatedMetadata);

                console.log(
                  '✏️ [VARIANT NAME] Updated variant name:',
                  newVariantName
                );
              }}
              placeholder={`e.g., ${getVariantExamples(watchLayer)[0]}`}
              helperText={`Examples: ${getVariantExamples(watchLayer).join(
                ', '
              )}`}
              sx={{
                bgcolor: 'background.paper',
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: 2,
                  },
                },
              }}
            />
          </Box>
        );
      })()}

      {/* Asset Details Section */}
      <Box mt={3}>
        <Grid container spacing={3}>
          {/* Creator's Description Input with Keyword Helper */}
          <Grid item xs={12}>
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  {getDynamicCreatorLabel()}
                  <Typography
                    component="span"
                    sx={{ color: 'error.main', ml: 0.5 }}
                  ></Typography>
                </Typography>
              </Box>

              <Typography
                variant="subtitle2"
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                Creator's description (Optional)
              </Typography>
              <input
                {...register('name', {
                  required: "Creator's Description is required",
                  maxLength: {
                    value: 200,
                    message:
                      "Creator's Description must not exceed 200 characters",
                  },
                  minLength: {
                    value: 3,
                    message:
                      "Creator's Description must be at least 3 characters",
                  },
                  validate: value => {
                    // CRITICAL: Ensure field is not empty or just whitespace
                    if (!value || !value.trim()) {
                      return "Creator's Description is required";
                    }
                    return true;
                  },
                })}
                value={watch('name') || ''}
                onChange={e => {
                  const newValue = e.target.value;
                  environmentSafeLog(
                    '[FIELD CHANGE] Creator Description field changed:',
                    {
                      newValue,
                      length: newValue.length,
                      timestamp: new Date().toISOString(),
                    }
                  );
                  setValue('name', newValue, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                }}
                placeholder={getLayerSpecificPlaceholder(watchLayer)}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '4px',
                  border: errors.name ? '1px solid #d32f2f' : '1px solid #ccc',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                }}
              />
              {errors.name && (
                <Typography
                  color="error"
                  variant="caption"
                  sx={{ display: 'block', mt: 0.5 }}
                >
                  {errors.name.message}
                </Typography>
              )}
            </Box>
          </Grid>

          {/* AI Metadata Generation - Conditional rendering */}
          {(() => {
            const showAI = uploadedFiles.length > 0;
            environmentSafeLog(
              '[STEP 3 DEBUG] AI Generation Visibility Check:',
              {
                hasUploadedFiles: uploadedFiles.length > 0,
                showAI,
                firstFileUrl: uploadedFiles[0]?.url,
                firstFileType: uploadedFiles[0]?.mimeType,
                watchLayer,
                watchCategoryCode,
                creatorDescription: watch('name') || '(empty)',
                watchSubcategoryCode,
              }
            );
            return showAI;
          })() && (
            <Grid item xs={12}>
              {/* DEBUG: Log props being passed to AIMetadataGenerator */}
              {(() => {
                const formComponents = watch('layerSpecificData.components');
                if (watchLayer === 'C') {
                  console.log(
                    '[AI PROPS DEBUG] Props being passed to AIMetadataGenerator:',
                    {
                      formComponents: formComponents?.length || 0,
                      formComponentsData: formComponents,
                      layer: watchLayer,
                    }
                  );
                }
                return null;
              })()}
              <AIMetadataGenerator
                key={`ai-generator-${watchLayer}-${
                  watch('layerSpecificData.components')?.length || 0
                }-stable`} // Stable key to preserve state
                file={getValues('files')?.[0]} // Pass File object directly for multipart/form-data (backward compatibility)
                getCurrentFile={() => {
                  // CRITICAL: Read current file on-demand, never cached
                  const currentFile = getValues('files')?.[0];
                  environmentSafeLog(
                    '[API REQUEST] Reading current file on-demand:',
                    {
                      hasFile: !!currentFile,
                      fileName: currentFile?.name,
                      fileSize: currentFile?.size,
                      fileType: currentFile?.type,
                      timestamp: new Date().toISOString(),
                    }
                  );
                  return currentFile;
                }}
                fileUrl={uploadedFiles[0]?.url} // Keep for backward compatibility/display
                fileName={getValues('files')?.[0]?.name}
                fileType={getValues('files')?.[0]?.type}
                layer={watchLayer}
                categoryCode={watchCategoryCode}
                subcategoryCode={watchSubcategoryCode}
                onDescriptionGenerated={onAIDescriptionGenerated}
                onTagsGenerated={onAITagsGenerated}
                onAdditionalMetadataGenerated={onAIAdditionalMetadataGenerated} // Phase 2A
                currentDescription={watch('description')}
                currentTags={watch('tags')}
                disabled={
                  !watchLayer || !watchCategoryCode || !watchSubcategoryCode
                }
                hideRegenerationButtons={false}
                // Enhanced AI Integration props
                shortDescription={watch('name')} // Creator's Description from the enhanced UI (for backward compatibility)
                getCurrentDescription={() => {
                  const currentValue = getValues('name');

                  environmentSafeLog(
                    '[API REQUEST] Reading current description on-demand:',
                    {
                      rawValue: currentValue,
                      rawType: typeof currentValue,
                      isString: typeof currentValue === 'string',
                      isObject:
                        typeof currentValue === 'object' &&
                        currentValue !== null,
                      timestamp: new Date().toISOString(),
                    }
                  );

                  // Ensure we return a string - handle all possible cases
                  let stringValue: string = '';

                  if (typeof currentValue === 'string') {
                    // Normal case: it's already a string
                    stringValue = currentValue;
                  } else if (currentValue == null) {
                    // Null or undefined: return empty string
                    stringValue = '';
                  } else if (typeof currentValue === 'object') {
                    // Object case: try to extract meaningful string value
                    // Check common object patterns
                    if (
                      'value' in currentValue &&
                      typeof (currentValue as any).value === 'string'
                    ) {
                      stringValue = (currentValue as any).value;
                    } else if (
                      'name' in currentValue &&
                      typeof (currentValue as any).name === 'string'
                    ) {
                      stringValue = (currentValue as any).name;
                    } else if (
                      'description' in currentValue &&
                      typeof (currentValue as any).description === 'string'
                    ) {
                      stringValue = (currentValue as any).description;
                    } else {
                      // Last resort: convert to string (but log warning)
                      console.warn(
                        '[API REQUEST] Unexpected object value for name field:',
                        currentValue
                      );
                      stringValue = '';
                    }
                  } else {
                    // Other types (number, boolean, etc.): convert to string
                    stringValue = String(currentValue);
                  }

                  environmentSafeLog('[API REQUEST] Final string value:', {
                    stringValue: stringValue,
                    length: stringValue.length,
                  });

                  return stringValue;
                }}
                categoryName={watch('categoryName')}
                subcategoryName={watch('subcategoryName')}
                layerName={watch('layerName')}
                thumbnailUrl={(uploadedFiles[0] as any)?.thumbnailUrl} // For video layers
                imageUrl={uploadedFiles[0]?.url} // For image layers (same as fileUrl for images)
                selectedComponents={watch('layerSpecificData.components')} // Use form data directly for C layer
                triggerGeneration={triggerAIGeneration} // External trigger for AI generation
                // 🛡️ INHERITANCE SUPPORT - Pass actual inheritance context to AI Contract Service for ALL layers
                inheritedValues={(() => {
                  try {
                    if (watchLayer === 'G') {
                      return (
                        watch('songMetadata' as any)?.baseAssetData
                          ?.songMetadata ||
                        watch('songMetadata' as any)?.baseAssetData?.aiMetadata
                          ?.songMetadata
                      );
                    }
                    if (watchLayer === 'S') {
                      return (
                        watch('starMetadata' as any)?.baseAssetData
                          ?.starMetadata ||
                        watch('starMetadata' as any)?.baseAssetData?.aiMetadata
                          ?.starsMetadata
                      );
                    }
                    if (watchLayer === 'L') {
                      return (
                        watch('looksMetadata' as any)?.baseAssetData
                          ?.looksMetadata ||
                        watch('looksMetadata' as any)?.baseAssetData?.aiMetadata
                          ?.looksMetadata
                      );
                    }
                    if (watchLayer === 'M') {
                      return (
                        watch('movesMetadata' as any)?.baseAssetData
                          ?.movesMetadata ||
                        watch('movesMetadata' as any)?.baseAssetData?.aiMetadata
                          ?.movesMetadata
                      );
                    }
                    if (watchLayer === 'W') {
                      return (
                        watch('worldsMetadata' as any)?.baseAssetData
                          ?.worldsMetadata ||
                        watch('worldsMetadata' as any)?.baseAssetData
                          ?.aiMetadata?.worldsMetadata
                      );
                    }
                    if (watchLayer === 'C') {
                      return (
                        watch('compositeMetadata' as any)?.baseAssetData
                          ?.compositeMetadata ||
                        watch('compositeMetadata' as any)?.baseAssetData
                          ?.aiMetadata?.compositeMetadata
                      );
                    }
                    return undefined;
                  } catch (error) {
                    console.warn(
                      '🛡️ [INHERITANCE] Error accessing inheritance context:',
                      error
                    );
                    return undefined;
                  }
                })()}
                baseAsset={(() => {
                  try {
                    if (watchLayer === 'G')
                      return watch('songMetadata' as any)?.baseAssetData;
                    if (watchLayer === 'S')
                      return watch('starMetadata' as any)?.baseAssetData;
                    if (watchLayer === 'L')
                      return watch('looksMetadata' as any)?.baseAssetData;
                    if (watchLayer === 'M')
                      return watch('movesMetadata' as any)?.baseAssetData;
                    if (watchLayer === 'W')
                      return watch('worldsMetadata' as any)?.baseAssetData;
                    if (watchLayer === 'C')
                      return watch('compositeMetadata' as any)?.baseAssetData;
                    return undefined;
                  } catch (error) {
                    console.warn(
                      '🛡️ [INHERITANCE] Error accessing base asset data:',
                      error
                    );
                    return undefined;
                  }
                })()}
                // 🏷️ VARIANT TAG ENHANCEMENT - Pass variant name for AlgoRhythm optimization
                variantName={(() => {
                  if (watchLayer === 'G') {
                    return watch('songMetadata')?.variantName;
                  }
                  if (watchLayer === 'S') {
                    return watch('starMetadata')?.variantName;
                  }
                  return undefined;
                })()}
                starName={(() => {
                  if (watchLayer === 'S') {
                    // Try to get star name from base asset data first
                    const baseAssetData = watch('starMetadata')?.baseAssetData;
                    if (baseAssetData?.starsMetadata?.celebrityName) {
                      return baseAssetData.starsMetadata.celebrityName;
                    }
                    if (baseAssetData?.starMetadata?.celebrityName) {
                      return baseAssetData.starMetadata.celebrityName;
                    }
                    if (
                      baseAssetData?.aiMetadata?.starsMetadata?.celebrityName
                    ) {
                      return baseAssetData.aiMetadata.starsMetadata
                        .celebrityName;
                    }
                    // Fallback to star name if available
                    return watch('starMetadata')?.starName;
                  }
                  return undefined;
                })()}
              />
            </Grid>
          )}
        </Grid>
      </Box>
    </Box>
  );

  // If disabled, show overlay
  if (disabled) {
    return (
      <Box sx={{ position: 'relative', opacity: 0.6, pointerEvents: 'none' }}>
        {content}
      </Box>
    );
  }

  // If loading, show loading overlay
  if (loading) {
    return (
      <Box sx={{ position: 'relative' }}>
        {content}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <Typography>Processing files...</Typography>
        </Box>
      </Box>
    );
  }

  return content;
};

export default FileUploadStep;
